import { zodResolver } from '@hookform/resolvers/zod';
import { CircleAlert } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { AvatarUploadInput } from '@/components/forms/avatar-upload';
import { CountrySelect } from '@/components/forms/country-dropdown';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MEDIA_URL } from '@/config';
import { Team } from '@/types';
import { formatFileSize } from '@/utils/file';

import { useUpdateTeam } from '../../api/updateTeam';

import { SettingsFormWrapper, SettingsHeader } from './common-settings';

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB

const formSchema = z.object({
  team_name: z
    .string()
    .min(3, 'Team name must be at least 3 characters')
    .max(50, 'Team name must be less than 50 characters')
    .regex(
      /^[a-zA-Z0-9 ]+$/,
      'Team name can only contain letters, numbers, and spaces',
    ),
  logo: z
    .union([z.instanceof(File), z.null(), z.undefined()])
    .refine(
      (value) => !(value instanceof File) || value.size <= MAX_FILE_SIZE,
      {
        message: `File size must be less than ${formatFileSize(MAX_FILE_SIZE)}`,
      },
    )
    .refine(
      (value) =>
        !(value instanceof File) ||
        ['image/jpeg', 'image/png'].includes(value.type),
      {
        message: 'Only JPEG or PNG files are allowed',
      },
    ),
  country: z.string().min(2, 'Country is required'),
});

export type TeamProfileFormValues = z.infer<typeof formSchema>;

interface TeamProfileFormProps {
  team: Team;
}

export function TeamProfileForm({ team }: TeamProfileFormProps) {
  const form = useForm<TeamProfileFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      team_name: team.team_name,
      logo: undefined,
      country: team.country,
    },
    mode: 'onChange',
  });

  const updateTeamMutation = useUpdateTeam();

  const onSubmit = async (values: TeamProfileFormValues) => {
    const formData = new FormData();
    formData.append('team_name', values.team_name);
    formData.append('country', values.country);
    if (values.logo instanceof File) {
      formData.append('logo', values.logo);
    } else if (values.logo === null) {
      formData.append('logo', 'null');
    }

    updateTeamMutation.mutate(
      { teamId: team.id, data: formData },
      {
        onSuccess: () => {
          toast.success('Team profile updated successfully!');
          form.reset(form.getValues());
        },
        onError: (error: any) => {
          if (error.data?.fields) {
            Object.entries(error.data.fields).forEach(([field, message]) => {
              form.setError(field as keyof TeamProfileFormValues, {
                type: 'server',
                message: message as string,
              });
            });
          }
        },
      },
    );
  };

  const handleError = (errors: any) => {
    for (const key in errors) {
      toast.error(errors[key]?.message || `Error in ${key}`, {
        icon: <CircleAlert size={16} className="text-destructive" />,
      });
    }
  };

  const { isValid, isDirty } = form.formState;

  return (
    <>
      <SettingsHeader
        title="Team Profile"
        description="Update your team’s profile details here."
      />
      <Form {...form}>
        <SettingsFormWrapper
          onSubmit={form.handleSubmit(onSubmit, handleError)}
          isPending={updateTeamMutation.isPending}
          isDirty={isDirty}
          isValid={isValid}
          submitLabel="Save Changes"
        >
          <AvatarUploadInput
            control={form.control}
            name="logo"
            label="Team Logo"
            description={`Upload a logo for your team (max ${formatFileSize(
              MAX_FILE_SIZE,
            )}, JPEG/PNG).`}
            accept="image/jpeg,image/png"
            defaultImage={
              team?.team_photo
                ? `${MEDIA_URL}/images/uploads/${team.team_photo}`
                : undefined
            }
            maxFileSize={MAX_FILE_SIZE}
          />
          <FormField
            control={form.control}
            name="team_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Team name</FormLabel>
                <FormControl>
                  <Input placeholder="Fnatic Academy" {...field} />
                </FormControl>
                <FormDescription>
                  This is your team display name.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <CountrySelect
            control={form.control}
            name="country"
            label="Country"
            description="Select the country of your team."
          />
        </SettingsFormWrapper>
      </Form>
    </>
  );
}
